import type { XMLElement } from '@xml-tools/ast';
import type { MacrosSchemaMetadata } from '@sap/ux-specification-types';
import Field from 'sap/ui/mdc/Field';

const customBuildingBlockElements = ['Column', 'Action', 'FilterField', 'ButtonGroup', 'FormElement'];

/**
 * Method to get value of xml attribute by attribute name.
 *
 * @param {XMLElement} node XML node
 * @param {string} name Attribute name
 * @returns {string | undefined} Value of xml attribute
 */
export function getAttribute(node: XMLElement, name: string): string | undefined {
    return node.attributes.find((attribute) => attribute.key === name)?.value;
}

/**
 * Method to get node by node name.
 *
 * @param {XMLElement} node XML node with children
 * @param {string} name Name of children node to search
 * @returns {XMLElement | undefined} Found children XML node
 */
export function getNodeByName(node: XMLElement, name: string): XMLElement | undefined {
    return node.subElements?.find((child: XMLElement) => child.name === name);
}

const NODE_DISPLAY_PRIORITY_ATTRIBUTES = {
    ButtonGroup: ['name'],
    Column: ['header'],
    FilterField: ['label'],
    Field: ['label'],
    FormElement: ['label']
} as const;

/**
 * Returns the first matching attribute value from descriptionProperties, or undefined.
 *
 * @param {XMLElement} node - The XML element to search for display attributes.
 * @returns {{ key: string; value: string } | undefined} The matching attribute object, or undefined if none found.
 */
function getDisplayAttribute(node: XMLElement): { key: string; value: string } | undefined {
    let descriptionProperties = ['id', 'header', 'key', 'title', 'text', 'label', 'name'];
    const nodeName = node.name;
    if (nodeName in NODE_DISPLAY_PRIORITY_ATTRIBUTES) {
        const priorities = NODE_DISPLAY_PRIORITY_ATTRIBUTES[nodeName];
        descriptionProperties = [...new Set([...priorities, ...descriptionProperties])];
    }
    const attributes = node.attributes || [];
    for (const prop of descriptionProperties) {
        const attr = attributes.find((attribute) => attribute.key === prop);
        if (attr) {
            return attr;
        }
    }
    return undefined;
}

/**
 * Extracts display info for UI rendering.
 * Returns "(#value)" for 'id' attributes, "(value)" for others, or undefined if not found.
 *
 * @param node XML element to search.
 * @returns Formatted display string or undefined.
 * @example
 * // { key: 'id', value: 'My_ID_Table' } => "(#My_ID_Table)"
 * // { key: 'title', value: 'Sales in Europe' } => "(Sales in Europe)"
 */
function extractDisplayInfo(node: XMLElement): string | undefined {
    const attributeFound = getDisplayAttribute(node);
    if (attributeFound) {
        return attributeFound.key === 'id' ? `(#${attributeFound.value})` : `(${attributeFound.value})`;
    }
    return undefined;
}

/**
 * Adds displayInfo to metadata if the node is a higher-level building block.
 * Skips custom XML elements like Column, Action, FilterField.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {string} name Node name.
 * @param {MacrosSchemaMetadata} metadata Metadata object to update.
 */
export function addDisplayInfoForBuildingBlock(node: XMLElement, name: string, metadata: MacrosSchemaMetadata): void {
    if (!customBuildingBlockElements.includes(name)) {
        const value = extractDisplayInfo(node);
        metadata.macroDisplayInfo = { name, value };
    }
}

/**
 * Returns a description and optional icon for a view node.
 * - For higher-level building blocks, description is "<name> #value" for 'id', or "<name> value" for others.
 * - For custom building blocks, uses the attribute value directly, or the node name if not found.
 *
 * @param {XMLElement} node Parsed XML element.
 * @param {string} name Node name.
 * @returns {{ description: string; icon?: string }} Object containing the description and optional icon.
 */
export function getViewNodeDescriptionAndIcon(node: XMLElement, name: string): { description: string; icon?: string } {
    const controlIconMap: Record<string, string> = {
        Table: 'Table',
        FilterBar: 'Filter',
        Chart: 'Chart',
        Form: 'Form',
        Field: 'FormField',
        Fields: 'FormField',
        FormElement: 'FormField',
        Page: 'Page',
        RichTextEditor: 'MightyPen',
        RichTextEditorWithMetadata: 'MightyPen'
    };
    let description: string;

    if (!customBuildingBlockElements.includes(name)) {
        const attr = getDisplayAttribute(node);
        if (attr) {
            const prefix = attr.key === 'id' ? '#' : '';
            description = `${name} ${prefix}${attr.value}`;
        } else {
            description = name;
        }
    } else {
        const attr = getDisplayAttribute(node);
        description = attr ? attr.value : name;
    }

    const icon = controlIconMap[name];
    return icon ? { description, icon } : { description };
}
